import type {
  DestinationConnectionSummary,
  JobDefinition,
  JobDestinationBinding,
} from '../shared/desktop-api';

/** A job's export destination was saved or changed; the plug re-reads which connections are in use. */
export const EXPORT_DESTINATION_CHANGED_EVENT = 'capturepoint-export-destination-changed';

export function announceExportDestinationChanged() {
  window.dispatchEvent(new Event(EXPORT_DESTINATION_CHANGED_EVENT));
}

/** Connection id → names of the jobs whose export goes to it. */
export type ConnectionUse = Map<string, string[]>;

export function connectionUse(
  jobs: Array<Pick<JobDefinition, 'id' | 'name'>>,
  bindings: Array<Pick<
    JobDestinationBinding,
    'jobId' | 'destinationKind' | 'connectionId' | 'status'
  > | null>,
): ConnectionUse {
  const names = new Map(jobs.map((job) => [job.id, job.name]));
  const use: ConnectionUse = new Map();
  for (const binding of bindings) {
    // A disconnected binding is what removing its connection leaves: it uses nothing.
    if (
      !binding ||
      binding.destinationKind !== 'content-central' ||
      !binding.connectionId ||
      binding.status === 'disconnected'
    )
      continue;
    const list = use.get(binding.connectionId) ?? [];
    list.push(names.get(binding.jobId) ?? binding.jobId);
    use.set(binding.connectionId, list);
  }
  return use;
}

/** The failing connections worth a status-bar alert: only those some job's export uses. */
export function alertingConnections<
  T extends Pick<DestinationConnectionSummary, 'connectionId' | 'lastFailureCode'>,
>(connections: T[], use: ConnectionUse): T[] {
  return connections.filter((item) => item.lastFailureCode && use.has(item.connectionId));
}

type ConnectionUseReader = {
  listJobs(): Promise<Array<Pick<JobDefinition, 'id' | 'name'>>>;
  getJobDestinationBinding(
    jobId: string,
  ): Promise<Pick<
    JobDestinationBinding,
    'jobId' | 'destinationKind' | 'connectionId' | 'status'
  > | null>;
};

/** Which jobs export to which connection. One job's unreadable binding does not hide the others. */
export async function readConnectionUse(api: ConnectionUseReader): Promise<ConnectionUse> {
  const jobs = await api.listJobs();
  const bindings = await Promise.all(
    jobs.map((job) => api.getJobDestinationBinding(job.id).catch(() => null)),
  );
  return connectionUse(jobs, bindings);
}

const nameList = (names: string[]) =>
  names.length <= 1
    ? names.join('')
    : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;

/** The Remove confirmation: plain, and naming the jobs whose export pauses. The question is its title. */
export function removeConnectionQuestion(
  connection: Pick<DestinationConnectionSummary, 'displayName'>,
  jobNames: string[],
): { title: string; message: string } {
  const ask = `Remove ${connection.displayName} from this computer?`;
  const effect =
    jobNames.length === 0
      ? 'No job exports to it.'
      : `${nameList(jobNames)} ${jobNames.length === 1 ? 'exports' : 'export'} to it. Export for ${jobNames.length === 1 ? 'that job' : 'those jobs'} will pause until you choose a new destination.`;
  return {
    title: ask,
    message: `${effect} Its saved key is deleted from this computer. Nothing in Content Central is deleted.`,
  };
}
